import { hubCtaTargetsForUser } from "@/lib/hubCtaTargets";
import { queryKeys } from "@/lib/queryClient";
import { fetchActiveHubCtas } from "@/services/hubCtas.service";
import type { HubCta } from "@/types/hubCtas";
import type { UserSubscriptionStateRow } from "@/types/userSubscriptionState";
import { useQuery } from "@tanstack/react-query";

const STALE_MS = 5 * 60 * 1000;

export function useHubCtas(opts: {
  userId: string | undefined;
  subscription: UserSubscriptionStateRow | null;
  /** Whether the subscription-state query has settled; null row + resolved = non-member. */
  subscriptionResolved: boolean;
  /** Supabase auth `user.created_at` (join date) — drives the trial window. */
  userCreatedAt?: string | null;
  /** Survey answers (instrument / level) from AuthContext. */
  preferenceMetadata?: Record<string, unknown> | null;
  /** Wait for the preferences row so trial users don't flash the regular CTAs. */
  preferencesLoading?: boolean;
  /** Apple (RevenueCat) trial state on iOS; pass `resolved` so we wait for it. */
  appleTrial?: { resolved: boolean; onTrial: boolean } | null;
  enabled?: boolean;
}) {
  const targets = hubCtaTargetsForUser({
    subscription: opts.subscription,
    userCreatedAt: opts.userCreatedAt,
    preferenceMetadata: opts.preferenceMetadata,
    appleTrial: opts.appleTrial,
  });
  const appleResolved = opts.appleTrial ? opts.appleTrial.resolved : true;

  return useQuery({
    queryKey: queryKeys.hubCtas(targets),
    queryFn: async (): Promise<HubCta[]> => {
      return fetchActiveHubCtas(targets);
    },
    enabled: Boolean(
      opts.enabled !== false &&
        opts.userId &&
        opts.subscriptionResolved &&
        !opts.preferencesLoading &&
        appleResolved &&
        targets.length > 0,
    ),
    staleTime: STALE_MS,
  });
}
